import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  TextField,
  Typography,
  Button,
  useTheme,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import { styled } from '@mui/system';
import RefreshIcon from '@mui/icons-material/Refresh';
import { message } from 'antd';
import { Base_url } from '../../services/BaseUrl';

const GradientPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(3),
  background: `linear-gradient(135deg, #f0f8ff, #e0ffff)`,
  boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
}));

const CustomCaptcha = ({ onVerify, onError }) => {
  const theme = useTheme();
  const [captchaText, setCaptchaText] = useState('');
  const [userInput, setUserInput] = useState('');
  const [isVerified, setIsVerified] = useState(false);

  const generateCaptcha = () => {
    const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    let result = '';
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptchaText(result);
    setIsVerified(false);
    setUserInput('');
    if (onError) onError(false);
  };

  useEffect(() => {
    generateCaptcha();
  }, []);

  const verifyCaptcha = () => {
    if (userInput === captchaText) {
      setIsVerified(true);
      if (onVerify) onVerify(true);
    } else {
      setIsVerified(false);
      if (onError) onError(true);
      generateCaptcha();
    }
  };

  const drawCaptcha = () => {
    return (
      <Box sx={{
        position: 'relative',
        width: '100%',
        height: 50,
        backgroundColor: theme.palette.grey[200],
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 1,
        overflow: 'hidden'
      }}>
        {captchaText.split('').map((char, index) => (
          <Typography
            key={index}
            variant="h6"
            sx={{
              display: 'inline-block',
              margin: '0 2px',
              color: theme.palette.getContrastText(theme.palette.grey[200]),
              fontWeight: 'bold',
              position: 'relative',
            }}
          >
            {char}
          </Typography>
        ))}
        {[...Array(5)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              top: Math.random() * 50,
              left: 0,
              right: 0,
              height: 1,
            }}
          />
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%', my: 2 }}>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Please verify you're human
      </Typography>

      {drawCaptcha()}

      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
        <Button
          size="small"
          onClick={generateCaptcha}
          startIcon={<RefreshIcon />}
          sx={{ minWidth: 40, mr: 1, padding: 2 }}
        >
          Refresh
        </Button>

        <TextField
          type="text"
          value={userInput}
          onChange={(e) => setUserInput(e.target.value)}
          variant="outlined"
          fullWidth
          placeholder="Enter the text above"
          size="small"
        />
      </Box>

      <Button
        fullWidth
        variant="outlined"
        onClick={verifyCaptcha}
        sx={{ mt: 1 }}
        disabled={!userInput}
      >
        Verify CAPTCHA
      </Button>

      {isVerified && (
        <Typography variant="body2" sx={{ mt: 1, color: 'success.main' }}>
          Verified successfully!
        </Typography>
      )}
    </Box>
  );
};

const ContactUpPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    contact: '',
    subject: '',
    message: '',
  });
  const [fieldErrors, setFieldErrors] = useState({
    name: false,
    email: false,
    contact: false,
    subject: false,
  });
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
  const [captchaError, setCaptchaError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  // const BASE_API = process.env.REACT_APP_BASE_API;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: false }));
    }
  };

  const validateForm = () => {
    const errors = {
      name: !formData.name.trim(),
      email: !formData.email.trim(),
      contact: !formData.contact.trim(),
      subject: !formData.subject.trim(),
    };

    setFieldErrors(errors);
    return !Object.values(errors).some(Boolean);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log(formData);
    console.log(process.env.BASE_API);


    // Validate form fields
    const isFormValid = validateForm();
    if (!isFormValid) {
      setSnackbar({
        open: true,
        message: 'Please fill in all required fields',
        severity: 'error',
      });
      return;
    }

    if (!isCaptchaVerified) {
      setCaptchaError(true);
      setSnackbar({
        open: true,
        message: 'Please complete the CAPTCHA verification',
        severity: 'error',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${Base_url.BASE_URL}/${Base_url.api_sendMail}`,

        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },

          body: JSON.stringify({
            ...formData,
            // to: 'catdzoro@gmail.com',
          }),
        });

      console.log(response);

      // message.success("Sent Successfully")
      setSnackbar({
        open: true,
        message: 'Message sent successfully!',
        severity: 'success',
      });

      const result = await response.json();

      if (response.ok) {
        setSnackbar({
          open: true,
          message: 'Message sent successfully!',
          severity: 'success',
        });
        // Reset form
        setFormData({
          name: '',
          email: '',
          contact: '',
          subject: '',
          message: '',
        });
        setIsCaptchaVerified(false);
      } else {
        throw new Error(result.message || 'Failed to send message');
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'An error occurred while sending the message',
        severity: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Container maxWidth="lg" sx={{ py: 10 }}>
      <Typography
        variant="h3"
        align="center"
        gutterBottom
        sx={{ fontWeight: 'bold', color: '#1976d2' }}
      >
        Talk to our friendly team
      </Typography>
      <Typography variant="body1" color="text.secondary" mb={3} align="center">
        We'll help you find the perfect plan, no matter your business size.
      </Typography>

      <Grid container spacing={6} justifyContent="center">
        {/* Form */}
        <Grid item xs={12} md={6}>
          <GradientPaper elevation={4}>
            <Typography variant="h6" gutterBottom sx={{ color: '#333' }}>
              Send us a message
            </Typography>
            <Box
              component="form"
              noValidate
              autoComplete="off"
              onSubmit={handleSubmit}
              sx={{
                mt: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <TextField
                label="Your Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                required
                error={fieldErrors.name}
                helperText={fieldErrors.name ? 'Name is required' : ''}
              />
              <TextField
                label="Your Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                required
                error={fieldErrors.email}
                helperText={fieldErrors.email ? 'Email is required' : ''}
              />
              <TextField
                label="Contact Number"
                name="contact"
                type="tel"
                value={formData.contact}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                required
                error={fieldErrors.contact}
                helperText={fieldErrors.contact ? 'Contact number is required' : ''}
              />
              <TextField
                label="Subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                required
                error={fieldErrors.subject}
                helperText={fieldErrors.subject ? 'Subject is required' : ''}
              />
              <TextField
                label="Your Message (optional)"
                name="message"
                multiline
                rows={4}
                value={formData.message}
                onChange={handleChange}
                variant="outlined"
                fullWidth
              />

              <CustomCaptcha
                onVerify={(verified) => setIsCaptchaVerified(verified)}
                onError={(error) => setCaptchaError(error)}
              />

              {captchaError && (
                <Typography color="error" variant="body2">
                  Please complete the CAPTCHA verification
                </Typography>
              )}

              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={!isCaptchaVerified || isSubmitting}
                sx={{ mt: 2 }}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Submit'
                )}
              </Button>
            </Box>
          </GradientPaper>
        </Grid>

        {/* Google Map */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              height: '100%',
              width: '100%',
              minHeight: 400,
              boxShadow: 3,
            }}
          >
            <iframe
              title="Our Location_in gmapa"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3926.3734464746925!2d74.9962608!3d13.0676253!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba4ab44c9143557%3A0x43cda69803dd22e3!2sShreeshTech%20Digital%20Solution!5e0!3m2!1sen!2sin!4v1717668900000!5m2!1sen!2sin"
              width="100%"
              height="100%"
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              style={{ border: 0 }}
            ></iframe>
          </Box>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ContactUpPage;